import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { CommonTooltipData, ToolTipType } from '../../../../assets/constants/common-tooltip-data';
import { CommonService } from '../../../services/common.service';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommunityEngagementService } from '../../../services/community-engagement.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription, timer } from 'rxjs';
import { TOOLTIP_DELAY } from '../../../../assets/constants/common-pool';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ToastMessageComponent } from '../../../shared/components/toast-message/toast-message.component';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'puc-common-tooltip',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgxSkeletonLoaderModule
  ],
  templateUrl: './common-tooltip.component.html',
  styleUrl: './common-tooltip.component.scss'
})

export class CommonTooltipComponent implements OnInit {
  avatarColors: { bg: string; text: string } | null = null;
  initials = '';
  isLoggedInUserDetails = false;
  private _data!: CommonTooltipData;
  ownerSub?: Subscription;
  ownerDetailsSub?: Subscription;
  lastFetchedOwnerId: string | null = null;
  hasValidCertification = false;

  @Input()
  set data(value: CommonTooltipData) {
    if (!value) return;
    this.hasValidCertification = this.commonService.isValidCertificationLevel(value.certification ?? '');
    this._data = {
      ...value,
      certification: this.commonService.getCertificationLevelLabel(value.certification ?? null),
      isFollowing: value.isFollowing ?? false,
      profileUrl: value.profileUrl ?? null,
      contributions: value.contributions ?? 0,
      isOwnerDetailsLoading: value.isOwnerDetailsLoading ?? true,
    };

    this.computeUserInitials(this._data.firstName ?? '', this._data.lastName ?? '');

    this.updateIsLoggedInUserDetailsFlag();
    if (this._data.toolTipType === ToolTipType.Acess_Info && 
        this._data.id && 
        this._data.isOwnerDetailsLoading &&
        this.lastFetchedOwnerId !== this._data.id) {

      this.fetchOwnerDetails();
    }

  }

  get data(): CommonTooltipData {
    return this._data;
  }

  constructor(private commonService: CommonService,
    private communityEngagement: CommunityEngagementService,
    public locaStorageService: LocalStorageService,
    private toastMessage: MatSnackBar
  ) { }

  ngOnInit() {
    // Set avatar colors based on first name
    if (this.data?.firstName) {
      this.data.color = this.commonService.getAvatarColors(this.data?.firstName);
    }
  }

  // Toast Message 
    showToast(type: string, message: string) {
      this.toastMessage.openFromComponent(ToastMessageComponent, {
        duration: 5000,
        data: {
          type: type,
          message: message
        },
      });
    }
    

  // This function is used to get the alert type date format
  formatDate(date: string | Date): string {
    return this.commonService.formatDate(date);
  }

  toggleFollow(event: Event) {
    event.stopPropagation(); // to prevent tooltip close
    if (!this.data?.userId) return;

    const previousState = this.data.isFollowing;
    this.data.isFollowing = !previousState;

    this.communityEngagement
    .followorUnfollowUser({ followingUserId: this.data.userId, isFollowing: this.data.isFollowing, action: this.data.isFollowing ? 'follow' : 'unfollow'})
    .subscribe({
      error: (error) => {
        this.data.isFollowing = previousState; // revert state on error
        // this.alertService.error(error?.error?.error ? error.error.error : 'An error occurred while updating follow status.');
        
      }
    });


  }

  fetchOwnerDetails(): void {
    this.lastFetchedOwnerId = this._data.id ?? null;
    this.ownerSub?.unsubscribe();
    this.ownerDetailsSub?.unsubscribe();

    this.ownerSub = timer(200).subscribe(() => {
      this.ownerDetailsSub = this.communityEngagement
        .getOwnerProfileDetails(this._data.id, this._data.componentType)
        .subscribe({
          next: (res: any) => {
            this.computeUserInitials(res?.userDetailsDto?.firstName ?? '', res?.userDetailsDto?.lastName ?? '');
            this.hasValidCertification = this.commonService.isValidCertificationLevel(res?.userDetailsDto?.certificateLevel);

            this._data = {
              ...this._data,
              firstName: res?.userDetailsDto?.firstName ?? null,
              lastName: res?.userDetailsDto?.lastName ?? null,
              certificateLevel: res?.userDetailsDto?.certificateLevel ?? null,
              certification: this.commonService.getCertificationLevelLabel(res?.userDetailsDto?.certificateLevel),
              profileUrl: res?.userDetailsDto?.profileUrl ?? null,
              isFollowing: res?.following ?? false,
              contributions: res?.contributions ?? 0,
              userId: res?.userDetailsDto?.id ?? '',
              isOwnerDetailsLoading: false
            };
            this.updateIsLoggedInUserDetailsFlag();
          },
          error: () => {
            this._data = { ...this._data, isOwnerDetailsLoading: false };
          }
        });
    });
  }

  onFollowMouseDown(event: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();
  }

  computeUserInitials(firstName: string, lastName: string ) {
    const f = firstName ?? '';
    const l = lastName ?? '';

    const { initials, colors } = this.commonService.getAvatarInitialsAndColors(f, l);

    this.initials = initials;
    this.avatarColors = colors;
  }

  updateIsLoggedInUserDetailsFlag(){
    const loggedInUserId = this.locaStorageService.getUserId();
    this.isLoggedInUserDetails = !!loggedInUserId && this._data.userId === loggedInUserId;
  }

  ngOnDestroy(): void {
    this.ownerSub?.unsubscribe();
    this.ownerDetailsSub?.unsubscribe();
    this.lastFetchedOwnerId = null;
  }

}
